/**
 * French for every sentence the server shows a person.
 *
 * Keyed by the exact English the API or worker writes — see `i18n.ts` for
 * why the English is the key. `server-text.test.ts` reads the server source
 * and fails when a user-facing sentence there has no entry here.
 *
 * Grouped by where the sentence comes from, so a change on the server is easy
 * to find here. Entries with `{placeholders}` match any interpolated value.
 */
import { compileServerText, type Locale, type ServerTextEntry } from "./i18n";

export const SERVER_TEXT: readonly ServerTextEntry[] = [
  // ── Imports (shared/imports.ts, services/importer, worker/handlers/import) ──
  { en: "Checking link", fr: "Vérification du lien" },
  { en: "Downloading", fr: "Téléchargement" },
  { en: "Saving", fr: "Enregistrement" },
  { en: "Import failed", fr: "Échec de l’import" },
  { en: "Looking for the video on {source}.", fr: "Recherche de la vidéo sur {source}." },
  {
    en: "Downloading the video from {source}. This page updates when it is done.",
    fr: "Téléchargement de la vidéo depuis {source}. Cette page se met à jour une fois terminé.",
  },
  { en: "Saving the video to your library.", fr: "Enregistrement de la vidéo dans votre bibliothèque." },

  // ── Importer errors (services/importer/index.ts — ImportError messages) ──
  {
    en: "Importing from links is not available on this server yet.",
    fr: "L’import depuis un lien n’est pas encore disponible sur ce serveur.",
  },
  {
    en: "Could not open that {source} short link. Try again in a moment.",
    fr: "Impossible d’ouvrir ce lien court {source}. Réessayez dans un instant.",
  },
  {
    en: "That {source} short link does not lead anywhere. Check it opens in your browser, or paste the full link.",
    fr: "Ce lien court {source} ne mène nulle part. Vérifiez qu’il s’ouvre dans votre navigateur, ou collez le lien complet.",
  },
  {
    en: "That {source} short link does not lead to a {platform} post with a video.",
    fr: "Ce lien court {source} ne mène pas à une publication {platform} contenant une vidéo.",
  },
  {
    en: "{source} returned something we could not read. Try again later.",
    fr: "{source} a renvoyé une réponse illisible. Réessayez plus tard.",
  },
  { en: "That link is not a single video.", fr: "Ce lien ne pointe pas vers une vidéo unique." },
  {
    en: "This live stream has only just ended and {source} is still processing it. Try again in a little while.",
    fr: "Ce direct vient de se terminer et {source} le traite encore. Réessayez d’ici peu.",
  },
  {
    en: "This is a live stream that is still running. Import it once it has finished.",
    fr: "Ce direct est toujours en cours. Importez-le une fois terminé.",
  },
  { en: "This video has not been published yet.", fr: "Cette vidéo n’a pas encore été publiée." },
  {
    en: "Videos must be under 10 hours. Longer ones cannot be transcribed.",
    fr: "Les vidéos doivent durer moins de 10 heures. Au-delà, elles ne peuvent pas être transcrites.",
  },
  {
    en: "{source} did not send a video file. Try again later.",
    fr: "{source} n’a pas envoyé de fichier vidéo. Réessayez plus tard.",
  },
  {
    en: "{source} sent an empty file. Try again later.",
    fr: "{source} a envoyé un fichier vide. Réessayez plus tard.",
  },
  {
    en: "{source} sent a format we cannot play.",
    fr: "{source} a envoyé un format que nous ne pouvons pas lire.",
  },
  {
    en: "{source} sent a file with no video or audio in it.",
    fr: "{source} a envoyé un fichier sans vidéo ni audio.",
  },
  {
    en: "This video is larger than 3 GB, which is the most that can be transcribed.",
    fr: "Cette vidéo dépasse 3 Go, la taille maximale pouvant être transcrite.",
  },
  {
    en: "YouTube is refusing downloads from our servers right now. Try again later.",
    fr: "YouTube refuse actuellement les téléchargements depuis nos serveurs. Réessayez plus tard.",
  },
  {
    en: "This video is private. Make it public or unlisted, then try again.",
    fr: "Cette vidéo est privée. Rendez-la publique ou non répertoriée, puis réessayez.",
  },
  {
    en: "This video is age-restricted, so it cannot be imported.",
    fr: "Cette vidéo est soumise à une limite d’âge et ne peut donc pas être importée.",
  },
  {
    en: "This video is only for the channel's members, so it cannot be imported.",
    fr: "Cette vidéo est réservée aux membres de la chaîne et ne peut donc pas être importée.",
  },
  {
    en: "This video is not available in the region our servers are in.",
    fr: "Cette vidéo n’est pas disponible dans la région où se trouvent nos serveurs.",
  },
  {
    en: "That LinkedIn post has no video we can import. It may be a text, image or document post, or it may not be public.",
    fr: "Cette publication LinkedIn ne contient aucune vidéo importable. Il s’agit peut-être d’une publication texte, image ou document, ou elle n’est pas publique.",
  },
  {
    en: "This video is unavailable — it may have been removed or made private.",
    fr: "Cette vidéo est indisponible — elle a peut-être été supprimée ou rendue privée.",
  },
  {
    en: "This video is copy-protected, so it cannot be imported.",
    fr: "Cette vidéo est protégée contre la copie et ne peut donc pas être importée.",
  },
  {
    en: "That link does not point to a video we can import.",
    fr: "Ce lien ne pointe pas vers une vidéo importable.",
  },
  {
    en: "This video needs an account to watch, so it cannot be imported. Only public and unlisted videos can.",
    fr: "Cette vidéo nécessite un compte pour être regardée et ne peut donc pas être importée. Seules les vidéos publiques et non répertoriées peuvent l’être.",
  },
  {
    en: "The platform is limiting how fast we can download. We will keep trying.",
    fr: "La plateforme limite notre vitesse de téléchargement. Nous continuons d’essayer.",
  },
  {
    en: "The download failed. We will try again; if it keeps failing, check the link still works.",
    fr: "Le téléchargement a échoué. Nous allons réessayer ; si l’échec persiste, vérifiez que le lien fonctionne toujours.",
  },

  // ── Import worker (worker/handlers/import.ts) and import routes (api/routes/imports.ts) ──
  {
    en: "This link is from a platform we cannot import from.",
    fr: "Ce lien provient d’une plateforme depuis laquelle nous ne pouvons pas importer.",
  },
  {
    en: "The download kept failing. Check the link still opens, then try again.",
    fr: "Le téléchargement a échoué à plusieurs reprises. Vérifiez que le lien s’ouvre toujours, puis réessayez.",
  },
  {
    en: "That is not a link to a {platforms} video. Paste the link to the video or post itself.",
    fr: "Ce n’est pas un lien vers une vidéo {platforms}. Collez le lien de la vidéo ou de la publication elle-même.",
  },
  {
    en: "Only videos imported from a link can be fetched again.",
    fr: "Seules les vidéos importées depuis un lien peuvent être récupérées à nouveau.",
  },
  {
    en: "This import is not in a failed state, so there is nothing to retry.",
    fr: "Cet import n’a pas échoué : il n’y a rien à relancer.",
  },

  // ── Bot status badges (services/recall/status.ts — meaningOf labels) ──
  { en: "Scheduled", fr: "Programmé" },
  { en: "Joining", fr: "Connexion" },
  { en: "Waiting to be let in", fr: "En attente d’admission" },
  { en: "In the call", fr: "Dans l’appel" },
  { en: "Recording", fr: "Enregistrement" },
  { en: "Recording refused", fr: "Enregistrement refusé" },
  { en: "In a breakout room", fr: "En sous-salle" },
  { en: "Processing", fr: "Traitement" },
  { en: "Failed", fr: "Échec" },
  { en: "No transcript", fr: "Pas de transcription" },
  { en: "Expired", fr: "Expiré" },
  { en: "Not let in", fr: "Non admis" },
  { en: "Removed from call", fr: "Retiré de l’appel" },
  { en: "Nobody joined", fr: "Personne n’est venu" },
  { en: "Already over", fr: "Déjà terminée" },
  { en: "Link not working", fr: "Lien invalide" },
  { en: "Link expired", fr: "Lien expiré" },
  { en: "Password needed", fr: "Mot de passe requis" },
  { en: "Meeting locked", fr: "Réunion verrouillée" },
  { en: "Sign-in required", fr: "Connexion requise" },
  { en: "Registration required", fr: "Inscription requise" },
  { en: "Recording disabled", fr: "Enregistrement désactivé" },

  // ── Bot status sentences (services/recall/status.ts — meaningOf messages) ──
  {
    en: "The notetaker is scheduled and will join at the meeting time.",
    fr: "L’assistant est programmé et rejoindra la réunion à l’heure prévue.",
  },
  { en: "The notetaker is joining the call.", fr: "L’assistant rejoint l’appel." },
  {
    en: "Waiting to be admitted — someone in the meeting needs to let the notetaker in.",
    fr: "En attente d’admission — quelqu’un dans la réunion doit laisser entrer l’assistant.",
  },
  { en: "In the call but not recording yet.", fr: "Dans l’appel, mais l’enregistrement n’a pas encore commencé." },
  { en: "Recording now.", fr: "Enregistrement en cours." },
  { en: "Recording permission granted.", fr: "Autorisation d’enregistrer accordée." },
  {
    en: "The host denied recording permission, so nothing was captured. Ask them to allow recording and send the notetaker again.",
    fr: "L’hôte a refusé l’autorisation d’enregistrer : rien n’a été capturé. Demandez-lui d’autoriser l’enregistrement et renvoyez l’assistant.",
  },
  {
    en: "The host denied recording permission, so nothing was captured. Ask them to allow it and send the notetaker again.",
    fr: "L’hôte a refusé l’autorisation d’enregistrer : rien n’a été capturé. Demandez-lui de l’autoriser et renvoyez l’assistant.",
  },
  {
    en: "The notetaker followed the call into a breakout room.",
    fr: "L’assistant a suivi l’appel dans une sous-salle.",
  },
  { en: "The notetaker left a breakout room.", fr: "L’assistant a quitté une sous-salle." },
  { en: "A breakout room opened during the call.", fr: "Une sous-salle s’est ouverte pendant l’appel." },
  { en: "A breakout room closed during the call.", fr: "Une sous-salle s’est fermée pendant l’appel." },
  { en: "The call ended. Preparing the recording.", fr: "L’appel est terminé. Préparation de l’enregistrement." },
  {
    en: "Downloading the recording and transcript.",
    fr: "Téléchargement de l’enregistrement et de la transcription.",
  },
  {
    en: "Transcript ready. Preparing the recording.",
    fr: "Transcription prête. Préparation de l’enregistrement.",
  },
  { en: "The notetaker could not record this meeting.", fr: "L’assistant n’a pas pu enregistrer cette réunion." },
  {
    en: "The recording was captured but could not be transcribed.",
    fr: "L’enregistrement a été capturé mais n’a pas pu être transcrit.",
  },
  {
    en: "The recording expired before we could download it. It is no longer available.",
    fr: "L’enregistrement a expiré avant que nous puissions le télécharger. Il n’est plus disponible.",
  },
  {
    en: "Nobody admitted the notetaker from the waiting room, so nothing was recorded. Send it again and admit it when it asks.",
    fr: "Personne n’a admis l’assistant depuis la salle d’attente : rien n’a été enregistré. Renvoyez-le et admettez-le lorsqu’il le demande.",
  },
  {
    en: "The notetaker waited in the lobby and gave up. Send it again and admit it when it asks.",
    fr: "L’assistant a attendu dans la salle d’attente puis a abandonné. Renvoyez-le et admettez-le lorsqu’il le demande.",
  },
  {
    en: "Someone removed the notetaker from the meeting, so the recording stops there.",
    fr: "Quelqu’un a retiré l’assistant de la réunion : l’enregistrement s’arrête là.",
  },
  {
    en: "The notetaker waited but nobody joined the meeting.",
    fr: "L’assistant a attendu, mais personne n’a rejoint la réunion.",
  },
  {
    en: "Everyone left, so the notetaker did too. Preparing the recording.",
    fr: "Tout le monde est parti, l’assistant aussi. Préparation de l’enregistrement.",
  },
  {
    en: "The meeting had already ended by the time the notetaker joined.",
    fr: "La réunion était déjà terminée lorsque l’assistant l’a rejointe.",
  },
  {
    en: "That meeting link did not work. Check it and send the notetaker again.",
    fr: "Ce lien de réunion n’a pas fonctionné. Vérifiez-le et renvoyez l’assistant.",
  },
  { en: "That meeting link has expired.", fr: "Ce lien de réunion a expiré." },
  {
    en: "The meeting needs a password. Paste the full invite link, which usually has it built in.",
    fr: "La réunion nécessite un mot de passe. Collez le lien d’invitation complet, qui l’inclut généralement.",
  },
  {
    en: "The meeting was locked, so the notetaker could not join.",
    fr: "La réunion était verrouillée : l’assistant n’a pas pu la rejoindre.",
  },
  {
    en: "This meeting only admits signed-in accounts, which the notetaker is not. Ask the host to allow guests.",
    fr: "Cette réunion n’admet que des comptes connectés, ce que l’assistant n’est pas. Demandez à l’hôte d’autoriser les invités.",
  },
  {
    en: "This Zoom meeting requires a registered email, which the notetaker cannot provide.",
    fr: "Cette réunion Zoom exige une adresse e-mail enregistrée, que l’assistant ne peut pas fournir.",
  },
  {
    en: "This Zoom meeting requires registration, so the notetaker could not join.",
    fr: "Cette réunion Zoom exige une inscription : l’assistant n’a pas pu la rejoindre.",
  },
  {
    en: "The Zoom account has local recording turned off, which the notetaker needs. A Zoom admin can enable it.",
    fr: "L’enregistrement local est désactivé sur le compte Zoom, alors que l’assistant en a besoin. Un administrateur Zoom peut l’activer.",
  },
  {
    en: "The notetaker left the call. Preparing the recording.",
    fr: "L’assistant a quitté l’appel. Préparation de l’enregistrement.",
  },
  { en: "Waiting for the notetaker.", fr: "En attente de l’assistant." },
  // An unmapped Recall status, spelled out with spaces. The status itself
  // stays in Recall's English.
  { en: "Status: {status}.", fr: "Statut : {status}." },

  // ── Bot sweep (worker/handlers/poll-bots.ts) ──
  {
    en: "The bot never joined or the meeting never started. Send a new bot to try again.",
    fr: "L’assistant n’a jamais rejoint la réunion, ou elle n’a jamais commencé. Envoyez un nouvel assistant pour réessayer.",
  },

  // ── API errors: generic (api/errors.ts, api/app.ts) ──
  { en: "Authentication required", fr: "Authentification requise" },
  { en: "You do not have permission to do that", fr: "Vous n’avez pas l’autorisation de faire cela" },
  { en: "Too many requests", fr: "Trop de requêtes" },
  { en: "The request body is not valid", fr: "Le contenu de la requête n’est pas valide" },
  { en: "Something went wrong on our end", fr: "Une erreur s’est produite de notre côté" },
  { en: "No such endpoint", fr: "Cette adresse d’API n’existe pas" },
  { en: "Accounts are created by invitation only.", fr: "Les comptes sont créés uniquement sur invitation." },
  {
    en: "Membership is managed through the workspace's People page.",
    fr: "Les membres se gèrent depuis la page Personnes de l’espace de travail.",
  },
  { en: "Rejected", fr: "Refusé" },

  // ── "… not found" (api notFound(), db requireFound()) — specific first ──
  { en: "Recording not found", fr: "Enregistrement introuvable" },
  { en: "Source recording not found", fr: "Enregistrement source introuvable" },
  { en: "Folder not found", fr: "Dossier introuvable" },
  { en: "Project not found", fr: "Projet introuvable" },
  { en: "Chat not found", fr: "Discussion introuvable" },
  { en: "Message not found", fr: "Message introuvable" },
  { en: "Invitation not found", fr: "Invitation introuvable" },
  { en: "Media not found", fr: "Média introuvable" },
  { en: "Member not found", fr: "Membre introuvable" },
  { en: "User not found", fr: "Utilisateur introuvable" },
  { en: "Tenant not found", fr: "Espace de travail introuvable" },
  { en: "Access request not found", fr: "Demande d’accès introuvable" },
  { en: "Speaker not found", fr: "Intervenant introuvable" },
  { en: "Utterance not found", fr: "Passage de la transcription introuvable" },
  { en: "Chapter version not found", fr: "Version des chapitres introuvable" },
  { en: "Summary version not found", fr: "Version du résumé introuvable" },
  { en: "Translation not found", fr: "Traduction introuvable" },
  { en: "Export not found", fr: "Export introuvable" },
  { en: "Credential not found", fr: "Clé API introuvable" },
  { en: "Provider not found", fr: "Fournisseur introuvable" },
  { en: "{resource} not found", fr: "{resource} introuvable" },

  // ── Rate limits (api/middleware/rate-limit.ts, api/routes/access.ts) ──
  {
    en: "Too many attempts. Try again in {seconds} seconds.",
    fr: "Trop de tentatives. Réessayez dans {seconds} secondes.",
  },
  { en: "Too many attempts. Wait a minute and try again.", fr: "Trop de tentatives. Patientez une minute, puis réessayez." },
  {
    en: "You are sending questions faster than the model can answer. Wait a moment.",
    fr: "Vous envoyez des questions plus vite que le modèle ne peut y répondre. Patientez un instant.",
  },
  { en: "Too many requests. Try again later.", fr: "Trop de requêtes. Réessayez plus tard." },

  // ── Session and workspace (api/middleware/tenant.ts) ──
  { en: "This account has been suspended", fr: "Ce compte a été suspendu" },
  { en: "Select a workspace to continue", fr: "Sélectionnez un espace de travail pour continuer" },
  { en: "You are not a member of this workspace", fr: "Vous n’êtes pas membre de cet espace de travail" },
  {
    en: "This workspace has been suspended. Contact support.",
    fr: "Cet espace de travail a été suspendu. Contactez l’assistance.",
  },
  { en: "This area is for platform operators", fr: "Cette zone est réservée aux opérateurs de la plateforme" },

  // ── Access requests and back-office (api/routes/access.ts, admin-*.ts) ──
  { en: "This request has already been reviewed.", fr: "Cette demande a déjà été examinée." },
  { en: "That workspace address is already in use.", fr: "Cette adresse d’espace de travail est déjà utilisée." },
  {
    en: "Type the workspace name exactly to confirm deletion.",
    fr: "Saisissez le nom exact de l’espace de travail pour confirmer la suppression.",
  },
  { en: "You cannot suspend your own account.", fr: "Vous ne pouvez pas suspendre votre propre compte." },
  {
    en: "Platform operators cannot be suspended from here.",
    fr: "Les opérateurs de la plateforme ne peuvent pas être suspendus depuis cette page.",
  },
  {
    en: "You cannot change your own platform access.",
    fr: "Vous ne pouvez pas modifier votre propre accès à la plateforme.",
  },
  {
    en: "Restore this account before granting platform access.",
    fr: "Rétablissez ce compte avant de lui donner accès à la plateforme.",
  },
  // Default ban reason stored on the user row.
  { en: "Suspended by a platform operator", fr: "Suspendu par un opérateur de la plateforme" },

  // ── Invitations (api/routes/invitations.ts) ──
  {
    en: "That person already belongs to a workspace. Invite them from inside it instead.",
    fr: "Cette personne appartient déjà à un espace de travail. Invitez-la plutôt depuis celui-ci.",
  },
  { en: "This invitation has already been used.", fr: "Cette invitation a déjà été utilisée." },
  {
    en: "This invitation has expired. Ask for a new one.",
    fr: "Cette invitation a expiré. Demandez-en une nouvelle.",
  },
  {
    en: "You already have an account. Sign in first, then open this link again.",
    fr: "Vous avez déjà un compte. Connectez-vous d’abord, puis rouvrez ce lien.",
  },
  { en: "Give your workspace a name.", fr: "Donnez un nom à votre espace de travail." },

  // ── Members and roles (api/routes/members.ts) ──
  {
    en: "This is the workspace's only owner. Make someone else an owner first.",
    fr: "C’est le seul propriétaire de l’espace de travail. Nommez d’abord quelqu’un d’autre propriétaire.",
  },
  { en: "You cannot change your own role", fr: "Vous ne pouvez pas modifier votre propre rôle" },
  { en: "You cannot change this member's role", fr: "Vous ne pouvez pas modifier le rôle de ce membre" },
  // The role is interpolated as its stored name; name each one properly.
  { en: "You cannot assign the user role", fr: "Vous ne pouvez pas attribuer le rôle de membre" },
  { en: "You cannot assign the creator role", fr: "Vous ne pouvez pas attribuer le rôle de créateur" },
  { en: "You cannot assign the admin role", fr: "Vous ne pouvez pas attribuer le rôle d’administrateur" },
  { en: "You cannot assign the super_admin role", fr: "Vous ne pouvez pas attribuer le rôle de propriétaire" },
  { en: "You cannot assign the {role} role", fr: "Vous ne pouvez pas attribuer le rôle {role}" },
  { en: "You cannot invite someone as user", fr: "Vous ne pouvez pas inviter quelqu’un en tant que membre" },
  { en: "You cannot invite someone as creator", fr: "Vous ne pouvez pas inviter quelqu’un en tant que créateur" },
  { en: "You cannot invite someone as admin", fr: "Vous ne pouvez pas inviter quelqu’un en tant qu’administrateur" },
  { en: "You cannot invite someone as super_admin", fr: "Vous ne pouvez pas inviter quelqu’un en tant que propriétaire" },
  { en: "You cannot invite someone as {role}", fr: "Vous ne pouvez pas inviter quelqu’un en tant que {role}" },
  {
    en: "Making someone an owner gives full control of this workspace, including over other admins and owners. Confirm to proceed.",
    fr: "Nommer quelqu’un propriétaire lui donne le contrôle total de cet espace de travail, y compris sur les autres administrateurs et propriétaires. Confirmez pour continuer.",
  },
  {
    en: "This takes away this person's ownership of the workspace. Confirm to proceed.",
    fr: "Cette personne ne sera plus propriétaire de l’espace de travail. Confirmez pour continuer.",
  },
  { en: "You cannot remove yourself from the workspace", fr: "Vous ne pouvez pas vous retirer vous-même de l’espace de travail" },
  { en: "You cannot remove this member", fr: "Vous ne pouvez pas retirer ce membre" },
  {
    en: "Removing an owner takes away their ownership and their access. Confirm to proceed.",
    fr: "Retirer un propriétaire lui retire la propriété et l’accès. Confirmez pour continuer.",
  },
  { en: "That person is already in this workspace.", fr: "Cette personne fait déjà partie de cet espace de travail." },

  // ── Folders (api/routes/projects.ts) ──
  { en: "A folder cannot contain itself.", fr: "Un dossier ne peut pas se contenir lui-même." },
  {
    en: "You cannot move a folder into one of its own subfolders.",
    fr: "Vous ne pouvez pas déplacer un dossier dans l’un de ses propres sous-dossiers.",
  },

  // ── Media (api/routes/media.ts) ──
  { en: "Not a valid media path.", fr: "Chemin de média invalide." },

  // ── Recordings (api/routes/recordings.ts) ──
  { en: "Only Zoom and Google Meet are supported", fr: "Seuls Zoom et Google Meet sont pris en charge" },
  {
    en: "This recording has no transcript yet, so there is nothing to work from.",
    fr: "Cet enregistrement n’a pas encore de transcription : il n’y a rien à exploiter.",
  },
  {
    en: "Only uploaded or imported videos can be transcribed again. A bot recording's transcript comes from the meeting itself.",
    fr: "Seules les vidéos importées peuvent être transcrites à nouveau. La transcription d’un enregistrement de l’assistant provient de la réunion elle-même.",
  },
  {
    en: "This video has not finished arriving, so there is nothing to transcribe yet.",
    fr: "Cette vidéo n’est pas encore entièrement arrivée : il n’y a rien à transcrire pour l’instant.",
  },
  { en: "This recording has no bot to stop.", fr: "Aucun assistant à arrêter pour cet enregistrement." },
  {
    en: "Chapters are not being generated for this recording.",
    fr: "Aucune génération de chapitres n’est en cours pour cet enregistrement.",
  },
  {
    en: "This recording has no media yet, so there is nothing to encode.",
    fr: "Cet enregistrement n’a pas encore de média : il n’y a rien à encoder.",
  },
  { en: "That time has already passed.", fr: "Cette heure est déjà passée." },
  {
    en: "Schedule at least 10 minutes ahead, or send the bot now.",
    fr: "Programmez au moins 10 minutes à l’avance, ou envoyez l’assistant maintenant.",
  },
  {
    en: "The transcript is already in {language} — there is nothing to translate.",
    fr: "La transcription est déjà en {language} — il n’y a rien à traduire.",
  },
  {
    en: "This recording has no stored media to download.",
    fr: "Cet enregistrement n’a aucun média stocké à télécharger.",
  },
  {
    en: "That language has no finished translation yet. Generate it first, then export.",
    fr: "Cette langue n’a pas encore de traduction terminée. Générez-la d’abord, puis exportez.",
  },
  { en: "This export is not finished yet.", fr: "Cet export n’est pas encore terminé." },
  // Default titles, stored when none was given.
  { en: "Untitled meeting", fr: "Réunion sans titre" },
  { en: "Untitled edit", fr: "Montage sans titre" },

  // ── Settings and keys (api/routes/settings.ts) ──
  {
    en: "{provider} rejected that key. Check it and try again.",
    fr: "{provider} a refusé cette clé. Vérifiez-la et réessayez.",
  },
  { en: "That is not a provider we support.", fr: "Ce fournisseur n’est pas pris en charge." },
  {
    en: "Add a {provider} key before browsing its models.",
    fr: "Ajoutez une clé {provider} avant de parcourir ses modèles.",
  },

  // ── Studio (api/routes/studio.ts, worker/handlers/publish.ts) ──
  {
    en: "A recording in this edit is still processing. Wait for it to finish.",
    fr: "Un enregistrement de ce montage est encore en traitement. Attendez la fin du traitement.",
  },
  { en: "There is nothing on the timeline to publish.", fr: "La ligne de temps est vide : il n’y a rien à publier." },
  {
    en: "The video is unchanged and no chapters were authored — there is nothing to publish.",
    fr: "La vidéo n’a pas changé et aucun chapitre n’a été rédigé — il n’y a rien à publier.",
  },
  { en: "This edit could not be read by the renderer.", fr: "Le moteur de rendu n’a pas pu lire ce montage." },
  {
    en: "The video renderer is unavailable on the server. Try publishing again later.",
    fr: "Le moteur de rendu vidéo est indisponible sur le serveur. Réessayez de publier plus tard.",
  },
  { en: "A source recording in this edit no longer exists.", fr: "Un enregistrement source de ce montage n’existe plus." },
  {
    en: "Nothing in this edit overlaps its sources any more.",
    fr: "Plus rien dans ce montage ne correspond à ses sources.",
  },
  {
    en: "Rendering this edit failed. The sources are untouched — try publishing again.",
    fr: "Le rendu de ce montage a échoué. Les sources sont intactes — réessayez de publier.",
  },
  {
    en: "Published without a transcript, as chosen. Transcription can be run from this page.",
    fr: "Publié sans transcription, comme demandé. La transcription peut être lancée depuis cette page.",
  },
  // A clip shorter than the minimum (shared/edl.ts timeline validation).
  { en: "A clip must be at least {ms}ms long.", fr: "Un extrait doit durer au moins {ms} ms." },

  // ── Version history labels (chapter sets and summary versions) ──
  { en: "Published manually", fr: "Publié manuellement" },
  { en: "Published from Studio", fr: "Publié depuis Studio" },
  { en: "Generated by {model}", fr: "Généré par {model}" },

  // ── Uploads (api/routes/uploads.ts, worker/handlers/retention.ts) ──
  {
    en: "Upload an MP4, MOV, WebM, or an audio file. Other formats need converting first.",
    fr: "Importez un fichier MP4, MOV, WebM ou audio. Les autres formats doivent d’abord être convertis.",
  },
  {
    en: "Files must be {size} or smaller. Larger recordings cannot be transcribed.",
    fr: "Les fichiers ne doivent pas dépasser {size}. Au-delà, les enregistrements ne peuvent pas être transcrits.",
  },
  {
    en: "Recordings must be under 10 hours. Longer ones cannot be transcribed.",
    fr: "Les enregistrements doivent durer moins de 10 heures. Au-delà, ils ne peuvent pas être transcrits.",
  },
  {
    en: "This upload has already finished or was cancelled. Start it again.",
    fr: "Cet import est déjà terminé ou a été annulé. Recommencez-le.",
  },
  {
    en: "No part of that file reached us. Please try the upload again.",
    fr: "Aucune partie de ce fichier ne nous est parvenue. Veuillez réessayer l’import.",
  },
  {
    en: "The upload is missing some of the file. Please try it again.",
    fr: "Une partie du fichier manque. Veuillez réessayer l’import.",
  },
  {
    en: "We could not find the uploaded file. Please try the upload again.",
    fr: "Nous ne trouvons pas le fichier importé. Veuillez réessayer l’import.",
  },
  { en: "The upload did not finish. Please try again.", fr: "L’import ne s’est pas terminé. Veuillez réessayer." },
  // Diarization labels for uploads (worker/handlers/transcribe.ts, recall/transcript.ts).
  { en: "Speaker {number}", fr: "Intervenant {number}" },

  // ── Transcription (services/asr describeAsrError, worker/handlers/transcribe.ts) ──
  {
    en: "The transcription key was rejected. Check ASR_API_KEY.",
    fr: "La clé de transcription a été refusée. Vérifiez ASR_API_KEY.",
  },
  {
    en: "The transcription key is not allowed to use speech-to-text. Check the key's endpoint restrictions.",
    fr: "La clé de transcription n’est pas autorisée à utiliser la reconnaissance vocale. Vérifiez les restrictions de la clé.",
  },
  { en: "The transcription account is out of quota.", fr: "Le compte de transcription a épuisé son quota." },
  { en: "The provider could not read this file.", fr: "Le fournisseur n’a pas pu lire ce fichier." },
  {
    en: "The transcription provider is rate limiting us.",
    fr: "Le fournisseur de transcription limite actuellement nos requêtes.",
  },
  {
    en: "Transcription failed. The recording is still playable.",
    fr: "La transcription a échoué. L’enregistrement reste lisible.",
  },
  {
    en: "Transcription is not configured for this workspace.",
    fr: "La transcription n’est pas configurée pour cet espace de travail.",
  },

  // ── Streaming encode (worker/handlers/transcode.ts) ──
  {
    en: "Streaming encoder is unavailable on the server. The recording still plays.",
    fr: "L’encodeur de lecture en continu est indisponible sur le serveur. L’enregistrement reste lisible.",
  },
  {
    en: "Encoding for streaming failed. The recording still plays.",
    fr: "L’encodage pour la lecture en continu a échoué. L’enregistrement reste lisible.",
  },

  // ── Exports (worker/handlers/export.ts, worker/index.ts) ──
  {
    en: "The recording's media is not available to export.",
    fr: "Le média de cet enregistrement n’est pas disponible pour l’export.",
  },
  {
    en: "Video processing is not available right now. Try again later.",
    fr: "Le traitement vidéo n’est pas disponible pour le moment. Réessayez plus tard.",
  },
  {
    en: "This recording has no transcript, so there are no subtitles to burn in.",
    fr: "Cet enregistrement n’a pas de transcription : il n’y a pas de sous-titres à incruster.",
  },
  {
    en: "That language has no finished translation. Generate it from the transcript panel first.",
    fr: "Cette langue n’a pas de traduction terminée. Générez-la d’abord depuis le panneau de transcription.",
  },
  {
    en: "No subtitle lines could be built from this transcript.",
    fr: "Aucune ligne de sous-titres n’a pu être créée à partir de cette transcription.",
  },
  {
    en: "Burning the subtitles failed. Try again, and contact support if it keeps failing.",
    fr: "L’incrustation des sous-titres a échoué. Réessayez, et contactez l’assistance si l’échec persiste.",
  },

  // ── Stalled jobs (worker/handlers/stalled.ts) ──
  {
    en: "Generation stopped before it finished — the server may have restarted. Try again.",
    fr: "La génération s’est arrêtée avant la fin — le serveur a peut-être redémarré. Réessayez.",
  },
  {
    en: "Encoding stopped before it finished — the server may have restarted. Try again.",
    fr: "L’encodage s’est arrêté avant la fin — le serveur a peut-être redémarré. Réessayez.",
  },
  {
    en: "The export stopped before it finished — the server may have restarted. Try again.",
    fr: "L’export s’est arrêté avant la fin — le serveur a peut-être redémarré. Réessayez.",
  },
  {
    en: "The import stopped before it finished — the server may have restarted. Try again.",
    fr: "L’import s’est arrêté avant la fin — le serveur a peut-être redémarré. Réessayez.",
  },

  // ── Chapters and summaries (worker/handlers/chapters.ts, summary.ts, translate.ts) ──
  {
    en: "There is not enough conversation here to divide into chapters.",
    fr: "Il n’y a pas assez d’échanges pour découper en chapitres.",
  },
  {
    en: "The model returned chapters that did not match the transcript. Try again, or pick a stronger model.",
    fr: "Le modèle a renvoyé des chapitres qui ne correspondent pas à la transcription. Réessayez, ou choisissez un modèle plus performant.",
  },
  {
    en: "This recording has no transcript, so there is nothing to summarise.",
    fr: "Cet enregistrement n’a pas de transcription : il n’y a rien à résumer.",
  },
  {
    en: "The model took too long to respond. Try again, or pick a faster model.",
    fr: "Le modèle a mis trop de temps à répondre. Réessayez, ou choisissez un modèle plus rapide.",
  },
  { en: "This recording has no transcript to translate.", fr: "Cet enregistrement n’a pas de transcription à traduire." },
  { en: "Translation was cancelled.", fr: "La traduction a été annulée." },

  // ── Generation errors (worker/handlers/generation-errors.ts) ──
  {
    en: "This recording was too long for the model to summarise in one response. Try a model with a larger output limit, or split the recording.",
    fr: "Cet enregistrement est trop long pour que le modèle le résume en une seule réponse. Essayez un modèle avec une limite de sortie plus élevée, ou divisez l’enregistrement.",
  },
  {
    en: "We asked the model for more than it can return in one request. That is a limit on our side, not your key — it has been logged.",
    fr: "Nous avons demandé au modèle plus qu’il ne peut renvoyer en une requête. C’est une limite de notre côté, pas de votre clé — l’incident a été consigné.",
  },
  {
    en: "The model key was rejected. Check it in Settings and try again.",
    fr: "La clé du modèle a été refusée. Vérifiez-la dans les paramètres et réessayez.",
  },
  {
    en: "The model provider is rate limiting this workspace. Try again shortly.",
    fr: "Le fournisseur du modèle limite les requêtes de cet espace de travail. Réessayez sous peu.",
  },
  {
    en: "The model rejected the request. If you recently changed models, check it supports this feature.",
    fr: "Le modèle a refusé la requête. Si vous avez récemment changé de modèle, vérifiez qu’il prend en charge cette fonctionnalité.",
  },
  {
    en: "The model provider had an error. Try again in a moment.",
    fr: "Le fournisseur du modèle a rencontré une erreur. Réessayez dans un instant.",
  },
  {
    en: "The model returned something we could not read. Try again, or switch models.",
    fr: "Le modèle a renvoyé une réponse illisible. Réessayez, ou changez de modèle.",
  },
  {
    en: "The model could not be reached. Check the key in Settings and try again.",
    fr: "Impossible de joindre le modèle. Vérifiez la clé dans les paramètres et réessayez.",
  },
  {
    en: "The model's {what} response was cut off before it finished.",
    fr: "La réponse {what} du modèle a été interrompue avant la fin.",
  },

  // ── Workspace model (services/llm/tenant.ts) ──
  {
    en: "No AI model is configured for this workspace. Add a key in Settings and try again.",
    fr: "Aucun modèle d’IA n’est configuré pour cet espace de travail. Ajoutez une clé dans les paramètres et réessayez.",
  },
  {
    en: "The workspace has no key for the selected provider. Check Settings and try again.",
    fr: "L’espace de travail n’a pas de clé pour le fournisseur sélectionné. Vérifiez les paramètres et réessayez.",
  },

  // ── Model catalogue descriptions (services/llm/anthropic.ts) ──
  {
    en: "Most capable. Best answers on long or subtle meetings.",
    fr: "Le plus performant. Les meilleures réponses sur les réunions longues ou subtiles.",
  },
  { en: "Balanced speed and quality. A good default.", fr: "Équilibre entre vitesse et qualité. Un bon choix par défaut." },
  {
    en: "Fastest and cheapest. Fine for short meetings.",
    fr: "Le plus rapide et le moins cher. Convient aux réunions courtes.",
  },

  // ── Chat (api/routes/chat.ts, services/llm) ──
  {
    en: "The workspace's AI key was rejected. Check it in Settings.",
    fr: "La clé d’IA de l’espace de travail a été refusée. Vérifiez-la dans les paramètres.",
  },
  {
    en: "The model is rate limited right now. Wait a moment and ask again.",
    fr: "Le modèle est limité en requêtes pour le moment. Patientez un instant et reposez votre question.",
  },
  // `detail` is the provider's own (English) explanation, passed through.
  {
    en: "The model rejected the request: {detail} Try a different model in Settings.",
    fr: "Le modèle a refusé la requête : {detail} Essayez un autre modèle dans les paramètres.",
  },
  {
    en: "The model rejected the request. Try a different model in Settings.",
    fr: "Le modèle a refusé la requête. Essayez un autre modèle dans les paramètres.",
  },
  { en: "The model stopped responding. Try again.", fr: "Le modèle a cessé de répondre. Réessayez." },
  {
    en: "Add an Anthropic or OpenRouter key in settings before using chat.",
    fr: "Ajoutez une clé Anthropic ou OpenRouter dans les paramètres avant d’utiliser la discussion.",
  },
  { en: "The configured provider has no key.", fr: "Le fournisseur configuré n’a pas de clé." },
  { en: "This recording has no transcript yet.", fr: "Cet enregistrement n’a pas encore de transcription." },
  // Default chat title before one is inferred.
  { en: "New chat", fr: "Nouvelle discussion" },

  // ── Request validation (shared/schemas.ts) ──
  {
    en: "Provide exactly one of clusterId or newSpeakerLabel.",
    fr: "Indiquez exactement un seul des champs clusterId ou newSpeakerLabel.",
  },
];

const translate = compileServerText(SERVER_TEXT);

/** The sentence in `locale`, or unchanged if it has no entry. */
export function localizeServerText(text: string, locale: Locale): string {
  return translate(text, locale);
}
